import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Activity, ArrowRight } from 'lucide-react';

const RED = '#E02128';
const BLUE = '#1E6FD9';

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-10% 0px' },
  transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] },
});

// Real figures from the DYSSC measurement programme, drifting quietly behind the section
const FRAGMENTS = [
  { t: '231.8 g', x: 5, y: 14, c: RED, s: 'lg', d: 26 },
  { t: '218.5 g', x: 86, y: 16, c: BLUE, s: 'lg', d: 30 },
  { t: '117.6', x: 14, y: 72, c: RED, s: 'md', d: 22, m: true },
  { t: '110.1', x: 78, y: 78, c: BLUE, s: 'md', d: 27, m: true },
  { t: '5.67 kg cm²', x: 30, y: 8, c: RED, s: 'sm', d: 31 },
  { t: '5.49 kg cm²', x: 60, y: 90, c: BLUE, s: 'sm', d: 24 },
  { t: '785 Hz', x: 8, y: 44, c: RED, s: 'md', d: 25, m: true },
  { t: '908 Hz', x: 90, y: 52, c: BLUE, s: 'md', d: 21, m: true },
  { t: '0.38 mm/kg', x: 24, y: 28, s: 'sm', d: 28 },
  { t: '0.61 mm/kg', x: 68, y: 26, s: 'sm', d: 23 },
  { t: '16 mm', x: 48, y: 6, s: 'sm', d: 33, m: true },
  { t: '243.5 mm', x: 3, y: 88, s: 'sm', d: 22 },
  { t: '239.1 mm', x: 92, y: 90, s: 'sm', d: 29 },
  { t: '+3.3%', x: 22, y: 54, c: RED, s: 'lg', d: 20, m: true },
  { t: '−6.4%', x: 76, y: 42, c: BLUE, s: 'lg', d: 32, m: true },
  { t: 'Δ 123 Hz', x: 56, y: 70, s: 'sm', d: 27 },
  { t: 'Σ m·r²', x: 12, y: 30, s: 'sm', d: 34 },
  { t: '∫ F dx', x: 84, y: 70, s: 'sm', d: 25 },
  { t: '± 0.1', x: 40, y: 94, s: 'sm', d: 21 },
  { t: '√', x: 50, y: 40, s: 'lg', d: 36, m: true },
  { t: '∂δ/∂F', x: 66, y: 8, s: 'sm', d: 24 },
  { t: '≈ 1.6×', x: 6, y: 62, s: 'sm', d: 29 },
];

const Spark = ({ c }) => (
  <svg width="92" height="30" viewBox="0 0 92 30" fill="none"><polyline points="0,26 14,20 26,22 40,12 54,16 68,6 92,9" stroke={c} strokeWidth="1.5" /><circle cx="68" cy="6" r="2" fill={c} /></svg>
);
const Bars = ({ c }) => (
  <svg width="64" height="32" viewBox="0 0 64 32" fill={c}><rect x="2" y="14" width="10" height="18" /><rect x="18" y="6" width="10" height="26" /><rect x="34" y="18" width="10" height="14" /><rect x="50" y="10" width="10" height="22" /></svg>
);
const Wave = ({ c }) => (
  <svg width="120" height="30" viewBox="0 0 120 30" fill="none"><path d="M0 15 C 10 0, 20 0, 30 15 S 50 30, 60 15 S 80 0, 90 15 S 110 30, 120 15" stroke={c} strokeWidth="1.5" /></svg>
);
const Hex = ({ c }) => (
  <svg width="40" height="44" viewBox="0 0 40 44" fill="none" stroke={c} strokeWidth="1.5"><polygon points="20,2 38,12 38,32 20,42 2,32 2,12" /><polygon points="20,12 29,17 29,27 20,32 11,27 11,17" /></svg>
);
const Curve = ({ c }) => (
  <svg width="80" height="48" viewBox="0 0 80 48" fill="none"><path d="M4 44 L76 8" stroke={RED} strokeWidth="1.5" /><path d="M4 44 C 24 20, 44 14, 76 10" stroke={BLUE} strokeWidth="1.5" /><path d="M4 4 V44 H76" stroke={c} strokeWidth="1" /></svg>
);
const Ring = ({ c }) => (
  <svg width="46" height="46" viewBox="0 0 46 46" fill="none" stroke={c} strokeWidth="1.5"><circle cx="23" cy="23" r="20" strokeDasharray="4 5" /><circle cx="23" cy="23" r="3" fill={c} /></svg>
);

const GRAPHS = [
  { G: Spark, x: 10, y: 8, c: 'rgba(255,255,255,0.45)', d: 30, m: true },
  { G: Wave, x: 58, y: 14, c: RED, d: 26 },
  { G: Bars, x: 86, y: 82, c: 'rgba(255,255,255,0.35)', d: 28, m: true },
  { G: Hex, x: 20, y: 84, c: RED, d: 23, m: true },
  { G: Curve, x: 74, y: 60, c: 'rgba(255,255,255,0.35)', d: 32, m: true },
  { G: Ring, x: 36, y: 62, c: BLUE, d: 27 },
  { G: Wave, x: 2, y: 74, c: BLUE, d: 29 },
  { G: Spark, x: 44, y: 22, c: BLUE, d: 25 },
  { G: Hex, x: 92, y: 6, c: 'rgba(255,255,255,0.35)', d: 34 },
];

const SIZE = { sm: 'text-[11px] sm:text-xs', md: 'text-base sm:text-lg', lg: 'text-2xl sm:text-3xl' };
const GLYPHS = '0123456789Δ∑∂√±×≈#%';

// Now and then one fragment's digits flicker through random glyphs, then settle on the real figure
const useScramble = (items) => {
  const [text, setText] = useState(items.map((f) => f.t));
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    const busy = new Map();
    const id = setInterval(() => {
      setText((prev) => {
        const next = [...prev];
        items.forEach((f, i) => {
          const left = busy.get(i);
          if (left) {
            next[i] = left === 1 ? f.t : f.t.split('').map((ch) => (/[0-9]/.test(ch) && Math.random() < 0.6 ? GLYPHS[Math.floor(Math.random() * GLYPHS.length)] : ch)).join('');
            left === 1 ? busy.delete(i) : busy.set(i, left - 1);
          } else if (busy.size < 2 && Math.random() < 0.002) busy.set(i, 7);
        });
        return next;
      });
    }, 110);
    return () => clearInterval(id);
  }, [items]);
  return text;
};

export const TechnicalsTeaser = () => {
  const text = useScramble(FRAGMENTS);
  return (
    <section id="technicals" className="relative overflow-hidden bg-[#0B0B0C] text-white border-y border-white/[0.06]" data-testid="technicals-teaser">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_45%_70%_at_0%_50%,rgba(224,33,40,0.1),transparent_60%),radial-gradient(ellipse_45%_70%_at_100%_50%,rgba(30,111,217,0.12),transparent_60%)]" />
      <div className="absolute inset-0 bg-texture opacity-30" />

      {/* light drifting data field */}
      <div className="absolute inset-0 select-none pointer-events-none font-mono" aria-hidden="true" data-testid="technicals-datafield">
        {FRAGMENTS.map((f, i) => (
          <span
            key={f.t}
            className={`tt-drift absolute whitespace-nowrap ${SIZE[f.s]} ${f.m ? '' : 'hidden sm:block'}`}
            style={{ left: `${f.x}%`, top: `${f.y}%`, color: f.c || 'rgba(255,255,255,0.5)', animationDuration: `${f.d}s`, animationDelay: `${-(i * 1.7) % f.d}s`, opacity: f.c ? 0.42 : 0.28 }}
          >
            {text[i]}
          </span>
        ))}
        {GRAPHS.map(({ G, x, y, c, d, m }, i) => (
          <span key={i} className={`tt-drift absolute ${m ? '' : 'hidden sm:block'}`} style={{ left: `${x}%`, top: `${y}%`, animationDuration: `${d}s`, animationDelay: `${-(i * 2.3) % d}s`, opacity: 0.4 }}>
            <G c={c} />
          </span>
        ))}
      </div>

      {/* dim well behind the copy so it reads cleanly */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_40%_65%_at_50%_50%,rgba(11,11,12,0.9),rgba(11,11,12,0.5)_55%,transparent_80%)]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 flex flex-col items-center text-center">
        <motion.p {...fade(0)} className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.32em] font-bold text-zinc-400">Technicals</motion.p>
        <motion.h2 {...fade(0.1)} className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.92] tracking-tight" data-testid="teaser-tagline">
          See for yourself<br /><span className="text-zinc-500">before you choose yours.</span>
        </motion.h2>
        <motion.p {...fade(0.2)} className="mt-5 text-zinc-400 text-sm sm:text-base leading-relaxed max-w-lg">
          Every DYSSC paddle was weighed, balanced, bent and struck in the lab. Weight, balance, swing and twist weight, face bend, impact sound — all of it, explained simply.
        </motion.p>
        <motion.div {...fade(0.3)} className="mt-8">
          <Link
            to="/technicals"
            className="group inline-flex items-center gap-3 rounded-full bg-white text-black font-bold uppercase tracking-[0.18em] text-sm sm:text-base pl-2 pr-7 py-2 shadow-[0_18px_50px_rgba(255,255,255,0.16)] hover:scale-[1.04] active:scale-[0.97] transition-transform"
            data-testid="teaser-technicals"
          >
            <span className="w-11 h-11 rounded-full bg-black text-white flex items-center justify-center"><Activity className="w-4 h-4" /></span>
            Technical analysis
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
